import React from 'react';

import { useTranslation } from '@modernloop/i18n';
import { formatDurationFromMinutes } from '@modernloop/shared/datetime';
import { useFeatureFlagBool } from '@modernloop/shared/feature-flag';
import { Alert, Box, Divider, FormControlLabel, Paper, Stack, Switch, TextField, Typography } from '@mui/material';

import { MaxDuration } from 'src/components/DurationMenu';
import useEqualSizeSxProps from 'src/components/utils/useEqualSizeSxProps';

import { CandidateAvailabilityOptions } from 'src/store/slices/candidate-availability-options';

import { KeywordTooltip } from 'src/views-new/SelfSchedule/KeywordTooltip';
import RequiredAdvanceNotice from 'src/views-new/SelfSchedule/RequiredAdvanceNotice';

import { DAY_CALC_FROM_INTERVIEW_PLAN, DURATION_CALC_FROM_INTERVIEW_PLAN } from 'src/constants';

import CandidateAvailabilityOptionsLabel from './CandidateAvailabilityOptionsLabel';
import DayCountPicker, { DayCount } from './DayCountPicker';
import DurationPicker from './DurationPicker';

export type AdvanceNoticeHours = 0 | 24 | 48 | 72;

interface CandidateAvailabilityOptionsPreferencesProps {
  options: CandidateAvailabilityOptions;
  candidateTimezone?: string;
  roundedMinsPerDay: number;
  disableToggles?: boolean;
  onNumberOfDaysChange: (value: DayCount) => void;
  onMinutesPerDayChange: (mins: number) => void;
  onMinTimeBlockMinutesChange: (mins: number) => void;
  onAdvanceNoticeHoursChange: (value: AdvanceNoticeHours) => void;
  onCandidateNoteBlur: (event: React.FocusEvent<HTMLInputElement>) => void;
  onScheduleOverFreeTimeChange: (value: boolean) => void;
  onScheduleOverRecruitingKeywordsChange: (value: boolean) => void;
  onScheduleOverAvailableKeywordsChange: (value: boolean) => void;
  onRespectLoadLimitChange: (value: boolean) => void;
  onBookIfPerfectScheduleFoundChange: (value: boolean) => void;
  alertJsx?: JSX.Element | null | boolean;
  allowUnsettingOptions?: boolean;
}

// eslint-disable-next-line modernloop/validate-component-definition.cjs
export function CandidateAvailabilityOptionsPreferences({
  options,
  candidateTimezone,
  disableToggles,
  roundedMinsPerDay,
  onNumberOfDaysChange,
  onMinutesPerDayChange,
  onMinTimeBlockMinutesChange,
  onAdvanceNoticeHoursChange,
  onCandidateNoteBlur,
  onRespectLoadLimitChange,
  onBookIfPerfectScheduleFoundChange,
  onScheduleOverFreeTimeChange,
  onScheduleOverRecruitingKeywordsChange,
  onScheduleOverAvailableKeywordsChange,
  allowUnsettingOptions,
  alertJsx,
}: CandidateAvailabilityOptionsPreferencesProps) {
  const { t } = useTranslation();
  const equalSizeSxProps = useEqualSizeSxProps();
  const bookPerfectScheduleUiEnabled = useFeatureFlagBool('TASK_AUTO_BOOK_PERFECT_SCHEDULE');

  const suggestedOptionsOn =
    options.canScheduleOverAvailableKeywords ||
    options.canScheduleOverRecruitingKeywords ||
    options.canScheduleOverFreeTime;

  return (
    <Stack direction="column" useFlexGap spacing={1} sx={{ overflowY: 'auto', overflowX: 'hidden', padding: '1px' }}>
      <>
        <Typography variant="subtitle1">
          {t('hiring-dashboard-candidates:candidates.availabilityPreferences.preferences')}
        </Typography>

        <KeywordTooltip
          title={t('hiring-dashboard-candidates:candidates.availabilityPreferences.freeTimesTitle')}
          description={t('hiring-dashboard-candidates:candidates.availabilityPreferences.freeTimesDescription')}
        >
          <FormControlLabel
            sx={{
              justifyContent: 'space-between',
            }}
            disabled={disableToggles}
            control={
              <Switch
                checked={!!options.canScheduleOverFreeTime}
                onChange={() => onScheduleOverFreeTimeChange(!options.canScheduleOverFreeTime)}
              />
            }
            labelPlacement="start"
            label={t('hiring-dashboard-candidates:candidates.availabilityPreferences.suggestOverFreeTime')}
          />
        </KeywordTooltip>

        <KeywordTooltip
          title={t('hiring-dashboard-candidates:candidates.availabilityPreferences.recruitingBlocksTitle')}
          description={t('hiring-dashboard-candidates:candidates.availabilityPreferences.recruitingBlocksDescription')}
        >
          <FormControlLabel
            sx={{
              justifyContent: 'space-between',
            }}
            disabled={disableToggles}
            control={
              <Switch
                checked={!!options.canScheduleOverRecruitingKeywords}
                onChange={() => onScheduleOverRecruitingKeywordsChange(!options.canScheduleOverRecruitingKeywords)}
              />
            }
            labelPlacement="start"
            label={t('hiring-dashboard-candidates:candidates.availabilityPreferences.suggestOverRecruitingBlocks')}
          />
        </KeywordTooltip>

        <KeywordTooltip
          title={t('hiring-dashboard-candidates:candidates.availabilityPreferences.availableTitle')}
          description={t('hiring-dashboard-candidates:candidates.availabilityPreferences.availableDescription')}
        >
          <FormControlLabel
            sx={{
              justifyContent: 'space-between',
            }}
            disabled={disableToggles}
            control={
              <Switch
                checked={!!options.canScheduleOverAvailableKeywords}
                onChange={() => onScheduleOverAvailableKeywordsChange(!options.canScheduleOverAvailableKeywords)}
              />
            }
            labelPlacement="start"
            label={t('hiring-dashboard-candidates:candidates.availabilityPreferences.suggestOverAvailableKeywords')}
          />
        </KeywordTooltip>

        {suggestedOptionsOn && (
          <KeywordTooltip
            title={t('hiring-dashboard-candidates:candidates.availabilityPreferences.loadLimitsTitle')}
            description={t('hiring-dashboard-candidates:candidates.availabilityPreferences.loadLimitsDescription')}
          >
            <FormControlLabel
              sx={{
                justifyContent: 'space-between',
              }}
              disabled={disableToggles}
              control={
                <Switch
                  checked={!options.shouldRespectLoadLimit}
                  onChange={() => onRespectLoadLimitChange(!options.shouldRespectLoadLimit)}
                />
              }
              labelPlacement="start"
              label={t('hiring-dashboard-candidates:candidates.availabilityPreferences.ignoreLoadLimits')}
            />
          </KeywordTooltip>
        )}

        <RequiredAdvanceNotice
          value={
            (options.advanceNoticeMinutes !== undefined ? options.advanceNoticeMinutes / 60 : 24) as AdvanceNoticeHours
          }
          onChange={onAdvanceNoticeHoursChange}
        />
      </>

      {alertJsx}

      <Stack direction="column" spacing={2}>
        <Typography variant="subtitle1">
          {t('hiring-dashboard-candidates:candidates.availabilityPreferences.howMuchAvailability')}
        </Typography>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
          sx={equalSizeSxProps.equalSizeChild}
        >
          <Box>
            <Typography>{t('hiring-dashboard-candidates:candidates.availabilityPreferences.days')}</Typography>
            <Typography color="text.secondary" variant="body2">
              {t('hiring-dashboard-candidates:candidates.availabilityPreferences.daysDescription')}
            </Typography>
          </Box>
          <DayCountPicker
            additionalOptions={
              allowUnsettingOptions
                ? [
                    {
                      id: String(DAY_CALC_FROM_INTERVIEW_PLAN),
                      value: t(
                        'hiring-dashboard-candidates:candidates.availabilityPreferences.calculateFromInterviewPlan'
                      ),
                    },
                  ]
                : []
            }
            days={options.numberOfDays as DayCount}
            onChange={onNumberOfDaysChange}
          />
        </Stack>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
          sx={equalSizeSxProps.equalSizeChild}
        >
          <Box>
            <Typography>
              {t('hiring-dashboard-candidates:candidates.availabilityPreferences.availabilityPerDay')}
            </Typography>
            <Typography color="text.secondary" variant="body2">
              {t('hiring-dashboard-candidates:candidates.availabilityPreferences.totalHoursNeeded')}
            </Typography>
          </Box>
          <DurationPicker
            dataTestId="availability-per-day-picker"
            additionalOptions={
              allowUnsettingOptions
                ? [
                    {
                      id: String(DURATION_CALC_FROM_INTERVIEW_PLAN),
                      value: t(
                        'hiring-dashboard-candidates:candidates.availabilityPreferences.calculateFromInterviewPlan'
                      ),
                    },
                  ]
                : []
            }
            duration={options.minutesPerDays}
            onChange={onMinutesPerDayChange}
          />
        </Stack>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
          sx={equalSizeSxProps.equalSizeChild}
        >
          <Box>
            <Typography>{t('hiring-dashboard-candidates:candidates.availabilityPreferences.timeBlocks')}</Typography>
            <Typography color="text.secondary" variant="body2">
              {t('hiring-dashboard-candidates:candidates.availabilityPreferences.timeBlocksDescription')}
            </Typography>
          </Box>
          <DurationPicker
            dataTestId="time-block-picker"
            additionalOptions={
              allowUnsettingOptions
                ? [
                    {
                      id: String(DURATION_CALC_FROM_INTERVIEW_PLAN),
                      value: t(
                        'hiring-dashboard-candidates:candidates.availabilityPreferences.calculateFromInterviewPlan'
                      ),
                    },
                  ]
                : []
            }
            duration={options.minimumTimeBlockMinutes}
            maxDuration={options.minutesPerDays as MaxDuration}
            onChange={onMinTimeBlockMinutesChange}
          />
        </Stack>
      </Stack>

      <Paper
        sx={{
          backgroundColor: (theme) => theme.palette.background.contrast,
        }}
      >
        <Box p={1}>
          <Typography variant="subtitle1">
            {t('hiring-dashboard-candidates:candidates.availabilityPreferences.requestSummary')}
          </Typography>
          <CandidateAvailabilityOptionsLabel
            options={options}
            candidateTimezone={candidateTimezone}
            roundedMinsPerDay={roundedMinsPerDay}
          />
        </Box>
      </Paper>

      {roundedMinsPerDay !== options.minutesPerDays && (
        <Stack spacing={2}>
          <Alert icon={false} severity="warning">
            {t('hiring-dashboard-candidates:candidates.availabilityPreferences.roundedUpWarning', {
              duration: formatDurationFromMinutes(roundedMinsPerDay),
            })}
          </Alert>
        </Stack>
      )}

      {bookPerfectScheduleUiEnabled && (
        // The section and its leading divider sit inside the rollout gate, so a flag-off request
        // flow keeps its current layout: the request summary is followed straight by the note.
        <>
          <Divider />
          <Stack direction="column" spacing={2}>
            <Typography variant="subtitle1">
              {t('hiring-dashboard-candidates:candidates.availabilityPreferences.automaticBookingSection')}
            </Typography>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems="center"
              justifyContent="space-between"
              spacing={1}
            >
              <Box>
                <Typography>
                  {t('hiring-dashboard-candidates:candidates.availabilityPreferences.bookIfPerfectScheduleFound')}
                </Typography>
                <Typography color="text.secondary" variant="body2">
                  {t('hiring-dashboard-candidates:candidates.availabilityPreferences.bookPerfectScheduleDescription')}
                </Typography>
              </Box>
              <Switch
                checked={!!options.bookIfPerfectScheduleFound}
                disabled={disableToggles}
                // The description replaces the old tooltip, so the switch carries the label itself.
                inputProps={{
                  'aria-label': t(
                    'hiring-dashboard-candidates:candidates.availabilityPreferences.bookIfPerfectScheduleFound'
                  ),
                }}
                onChange={() => onBookIfPerfectScheduleFoundChange(!options.bookIfPerfectScheduleFound)}
              />
            </Stack>
          </Stack>
        </>
      )}

      <Divider />

      <Stack spacing={1}>
        <Typography>
          <strong style={{ fontWeight: 600 }}>
            {t('hiring-dashboard-candidates:candidates.availabilityPreferences.noteToCandidate')}
          </strong>{' '}
          {t('hiring-dashboard-common:common.optional')}
        </Typography>
        <TextField
          fullWidth
          multiline
          maxRows={4}
          minRows={4}
          defaultValue={options.candidateNote}
          placeholder={t('hiring-dashboard-candidates:candidates.availabilityPreferences.notePlaceholder')}
          onBlur={onCandidateNoteBlur}
        />
      </Stack>
    </Stack>
  );
}
